import { QueryClient } from "@tanstack/react-query";
import { ExtendedAccount } from "@common/types/reports/types";
import { accountTypeToAccountClass } from "@common/constants/domains/accounting/helpers";
import { getAccounts } from "@api/accounting.api";
import { getAccountsQueryKey } from "@hooks/useAccounts";
import { DraftAccount } from "../draftState";
import { NestedEditorRow } from "../nested/nestedRowTypes";

const LOAD_ACCOUNTS_FAILURE_MESSAGE = "Could not load accounts. Try again.";

export type RunMergePreparationResult =
    | { kind: "aborted" }
    | { kind: "prepared"; account: ExtendedAccount };

interface RunMergePreparationParams {
    row: NestedEditorRow;
    // The content hook's client, not the queryClient.ts singleton, so this
    // warms the same cache the combobox reads.
    queryClient: QueryClient;
    accountsById: Map<string, ExtendedAccount>;
    // Ref-updated: the draft can change during whenIdle.
    getDraftAccount: (id: string) => DraftAccount | undefined;
    getSessionToken: () => number;
    isSessionCurrent: (token: number) => boolean;
    isSessionBlocked: () => boolean;
    whenIdle: () => Promise<void>;
    setIsPreparingMerge: (value: boolean) => void;
    onError: (message: string) => void;
}

export const runMergePreparation = async ({
    row,
    queryClient,
    accountsById,
    getDraftAccount,
    getSessionToken,
    isSessionCurrent,
    isSessionBlocked,
    whenIdle,
    setIsPreparingMerge,
    onError,
}: RunMergePreparationParams): Promise<RunMergePreparationResult> => {
    const seed = row.account;
    if (!seed) {
        return { kind: "aborted" };
    }
    const token = getSessionToken();
    // A name-cell blur from the same pointer sequence can enqueue before
    // isWriting re-renders.
    await whenIdle();
    if (!isSessionCurrent(token) || isSessionBlocked()) {
        return { kind: "aborted" };
    }

    setIsPreparingMerge(true);
    try {
        // staleTime 0 overrides the app's 5s default. refetchQueries would
        // resolve without fetching on a cold cache, making the failure toast
        // unreachable.
        await queryClient.fetchQuery({
            queryKey: getAccountsQueryKey(seed.entityId),
            queryFn: () => getAccounts(seed.entityId),
            staleTime: 0,
        });
    } catch {
        setIsPreparingMerge(false);
        onError(LOAD_ACCOUNTS_FAILURE_MESSAGE);
        return { kind: "aborted" };
    }

    if (!isSessionCurrent(token) || isSessionBlocked()) {
        setIsPreparingMerge(false);
        return { kind: "aborted" };
    }

    const chartAccount = accountsById.get(seed.id);
    const draftAccount = getDraftAccount(seed.id);
    if (!chartAccount || !draftAccount) {
        setIsPreparingMerge(false);
        return { kind: "aborted" };
    }

    // Overlay this session's rename / type-changing drop so header and target
    // class match what's on screen. Preview/execute still key off id + entityId.
    return {
        kind: "prepared",
        account: {
            ...chartAccount,
            name: draftAccount.name,
            isDisabled: draftAccount.isDisabled,
            type: draftAccount.type,
            class: accountTypeToAccountClass[draftAccount.type],
        },
    };
};
